import React, { useEffect, useMemo } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';

import { getDeployedEnvType, getNextMiApolloClient, useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { Layout, respGridUtil, prePageCall } from '@marriott/mi-headless-utils';
import {
  constants,
  MIN_PAGE_HEIGHT,
  backgroundColorMapping,
  IMAGE_URL_DOMAIN,
  PAGES,
  campaignOffersLimit,
} from '@marriott/mi-awp-components/constants';
import { isEmpty, PageParamsContext, getSubDirectoryLocale } from '@marriott/mi-awp-components';
import operationSignatures from '../../../_constants/operationSignatures.json';
import { campaignBannerAndOverviewDetails } from '@marriott/mi-awp-graphql';
import Cookies from 'js-cookie';

const { NEXT_PUBLIC_AEM_PATH, NEXT_PUBLIC_DEFAULT_LANG = 'en-US' } = process.env;
const { ENV_DEV, REDIRECT_URL } = process.env;
const isDev = process.env['NODE_ENV'] === ENV_DEV;

const mockModel = async () => {
  if (isDev) {
    // nx-ignore-next-line
    const { campaignPageModel } = await import('@marriott/mi-awp-components/localModel');
    return campaignPageModel;
  }
  return {};
};

const { ITEM_PATH_ROOT, ITEM_PATH_FOOTER, ITEM_PATH_HEADER } = constants;
const { CAMPAIGN_PAGE } = PAGES;
const sessionID = Cookies.get('sessionID');
const currentTimestamp = Date.now();
const currentDateTimeStamp = new Date(currentTimestamp).getTime();

// final data would have to come from 2 calls:
// 1. Page level call from AEM - that will populate the labels on components
// 2. Functional data for components like list of amenities and other related information
// import jsonContent from '../../mock/label-info.json';

export default function CampaignPage({
  model,
  deployedEnvType,
  isAuthorMode,
  campaignId,
  campaignData,
  campaignDataError,
  pageUrl,
  currentLocale,
  requestUri,
  cookies,
}) {
  const MiApolloClient = useNextMiApolloClient(operationSignatures, deployedEnvType);

  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const responsiveGridHeaderModel = respGridUtil(model?.cqItems?.root?.[':items']?.['header-responsivegrid']);
  const responsiveGridFooterModel = respGridUtil(model?.cqItems?.root?.[':items']?.['footer-responsivegrid']);

  const { metaNames, metaProperties, staticComponentClientLibsJS, staticComponentClientLibsCSS, gridCss, bgColor } =
    model;
  let { title, canonical, hrefLangList } = model;

  const fallbackImgPath =
    model?.cqItems?.root?.[':items']?.responsivegrid?.[':items']?.herobannercarousel?.dynamicMedia || {};

  const contextValue = useMemo(
    () => ({
      userId: cookies.UserIdToken,
      currentPage: CAMPAIGN_PAGE,
      campaignId: campaignId,
      campaignDataResponse: campaignData,
      campaignDataError: campaignDataError,
      currentLocale: currentLocale,
    }),
    [campaignId, cookies, campaignData, campaignDataError]
  );

  // Updating the background color for Home Page
  const bgColorSearch = backgroundColorMapping[bgColor];

  const updateMetaProperties = () => {
    // Set Page title
    let dynamicTitle = title;
    if (dynamicTitle?.includes('[Campaign Name]')) {
      dynamicTitle = updateCampaignTitle(title);
    }
    if (title) title = dynamicTitle;

    // Description
    const descriptionKey = metaNames?.find((item: { name: string; value: string }) => item.name === 'description');
    let description = descriptionKey?.value;
    if (description?.includes('[Overview Title]')) {
      description = updateCampaignDesc(description);
    }

    // Update Meta Names
    metaNames?.map((item: { name: string; value: string }) => {
      if (item.name === 'description') {
        item.value = description;
      }
      if (item?.name === 'url') {
        const urlSplit = item?.value.split('//');
        const hostUrl = urlSplit?.[1]?.split('/');
        item.value = urlSplit?.[0] + '//' + hostUrl?.[0] + '/' + hostUrl?.[1] + pageUrl + '.mi';
      }
    });

    // Update Meta Properties
    metaProperties?.map((item: { name: string; value: string }) => {
      if (item?.name === 'og:title') item.value = dynamicTitle;

      if (item?.name === 'og:description') {
        item.value = description;
      }

      if (item?.name === 'og:url') {
        const urlSplit = item?.value.split('//');
        const hostUrl = urlSplit?.[1]?.split('/');
        item.value = urlSplit?.[0] + '//' + hostUrl?.[0] + '/' + hostUrl?.[1] + pageUrl + '.mi';
      }
      if (item?.name === 'og:image') {
        if (campaignData?.heroBanner?.imageUrls?.panoramicHorizontal)
          item.value = `${IMAGE_URL_DOMAIN}${campaignData?.heroBanner?.imageUrls?.panoramicHorizontal}`;
        else item.value = fallbackImgPath?.assetPath;
      }
    });
  };

  const updateCampaignTitle = (title: string) => {
    const updatedTitle = title.replace('[Campaign Name]', campaignData?.title);
    return updatedTitle;
  };

  const updateCampaignDesc = (description: string) => {
    const updatedDescription = description.replace('[Overview Title]', campaignData?.overviewTitle);
    return updatedDescription;
  };

  updateMetaProperties();

  //  Using this to Prevent The Click event default action
  useEffect(() => {
    const headerAccBtn = document.getElementsByClassName('m-header__acnt t-global-nav-items font-nav-items');
    if (!headerAccBtn) return;
    const headerAccBtnMain = Array?.prototype?.filter?.call(headerAccBtn, element => element?.nodeName === 'LI');
    if (!headerAccBtnMain) return;

    const handlePreventDefault = (event: MouseEvent) => {
      event.preventDefault();
    };

    headerAccBtnMain[0]?.addEventListener('click', handlePreventDefault);
    return () => headerAccBtnMain[0]?.removeEventListener('click', handlePreventDefault);
  }, []);

  const updateCanonicalValue = () => {
    const urlSplit = canonical?.split('//');
    const hostUrl = urlSplit?.[1]?.split('/');
    canonical = urlSplit?.[0] + '//' + hostUrl?.[0] + '/' + hostUrl?.[1] + requestUri + '.mi';
  };

  const updateHrefLangList = () => {
    hrefLangList = hrefLangList?.map(hrefLangItem => {
      return {
        ...hrefLangItem,
        href: hrefLangItem?.href?.replace('/dining/campaign/[Campaign Id]', requestUri),
      };
    });
  };

  updateHrefLangList();
  updateCanonicalValue();

  return (
    <ApolloProvider client={MiApolloClient}>
      <PageParamsContext.Provider value={contextValue}>
        <Layout
          metaNames={metaNames}
          metaProps={metaProperties}
          title={title}
          staticComponentClientLibsCSS={staticComponentClientLibsCSS}
          staticComponentClientLibsJS={staticComponentClientLibsJS}
          isAuthorMode={isAuthorMode}
          gridCss={gridCss}
          canonical={canonical}
          hreflangList={hrefLangList}
        >
          <section>
            {responsiveGridHeaderModel && (
              <ResponsiveGrid
                {...responsiveGridHeaderModel}
                model={responsiveGridHeaderModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_HEADER}
              />
            )}
            <div style={{ backgroundColor: bgColorSearch, minHeight: MIN_PAGE_HEIGHT }}>
              <ResponsiveGrid
                {...responsiveGridModel}
                model={responsiveGridModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_ROOT}
              />
            </div>
            {responsiveGridFooterModel && (
              <ResponsiveGrid
                {...responsiveGridFooterModel}
                model={responsiveGridFooterModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_FOOTER}
              />
            )}
          </section>
        </Layout>
      </PageParamsContext.Provider>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const awpSpecificData = async () => {
  //any page specific code can be written here.
  const deployedEnvType = getDeployedEnvType();

  return {
    operationSignatures: operationSignatures,
    deployedEnvType,
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps = async (context: { [key: string]: any }, params: any) => {
  const response = prePageCall(awpSpecificData, mockModel, false, true, true);
  const componentProps = await response(context).then(data => data);
  const campaignId = context?.params?.campaign;
  const deployedEnvType = getDeployedEnvType();
  const requestId = context?.req.headers['x-request-id'];
  const MiApolloClient = getNextMiApolloClient(operationSignatures, deployedEnvType);
  const currentLocale = context?.req?.['headers']?.['accept-language'] ?? 'en-US';
  const locale = componentProps?.props?.currentLocale?.split('_').join('-') || 'en-US';
  const subLocale = getSubDirectoryLocale(currentLocale).replace('/', '');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headers: any = {
    // Any header, including x-request-id, can be passed in args with query.
    // If you don't pass it in the authLink will generate a random ID.
    'x-request-id': requestId,
    'accept-language': locale,
    'x-b3-traceId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
    'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
    'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
    Cookie: `sessionID=${context?.req?.cookies?.sessionID}}`,
  };

  if (deployedEnvType === 'lower') {
    headers['Accept-Encoding'] = 'gzip, deflate, br, zstd';
  }

  if (componentProps?.props?.model && !isEmpty(componentProps?.props?.model?.cqPath)) {
    try {
      const { data } = await MiApolloClient.query({
        query: campaignBannerAndOverviewDetails,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        variables: {
          input: {
            campaignId: campaignId,
            offset: 0,
            limit: campaignOffersLimit,
            facets: {
              terms: [],
            },
          },
        },
        context: {
          headers: headers,
        },
      });

      if (data && data?.diningOfferCampaignDetails !== null) {
        return {
          props: {
            operationSignatures: operationSignatures,
            ...componentProps.props,
            deployedEnvType: componentProps?.props?.apolloEnvVars?.DEPLOYED_ENV_TYPE,
            campaignId: campaignId,
            campaignData: data?.diningOfferCampaignDetails,
            campaignDataError: false,
            pageUrl: params?.req?.url || '',
          },
        };
      } else {
        return {
          redirect: {
            destination: `/${subLocale}${REDIRECT_URL}`,
            permanent: false,
          },
        };
      }
    } catch (error) {
      return {
        redirect: {
          destination: `/${subLocale}${REDIRECT_URL}`,
          permanent: false,
        },
      };
    }
  } else {
    return {
      redirect: {
        destination: `/${subLocale}${REDIRECT_URL}`,
        permanent: false,
      },
    };
  }
};
